import { useState } from "react";
import { Loader2, ShieldAlert } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls = "w-full rounded-full bg-white border border-sand-200 px-4 py-3 text-sm outline-none focus:border-ink transition-colors";

// Set or change the signed-in person's password. Asks for the current one only when a real
// (non-temporary) password already exists — that's exactly the rule the backend enforces.
export function PasswordForm({ onDone, submitLabel, testPrefix = "password-form" }) {
  const { user, setUser } = useAuth();
  const needsCurrent = !!user?.has_password && !user?.must_change_password;
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { setErr("Use at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Those passwords don't match."); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/auth/password/change", { current_password: current, new_password: pw });
      setUser({ ...user, has_password: true, must_change_password: false });
      setCurrent(""); setPw(""); setPw2("");
      onDone?.();
    } catch (e2) {
      setErr(errMsg(e2, "Couldn't save that password."));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid={testPrefix}>
      {needsCurrent && (
        <input data-testid={`${testPrefix}-current`} type="password" autoComplete="current-password" value={current}
          onChange={(e) => setCurrent(e.target.value)} placeholder="Current password" className={inputCls} />
      )}
      <input data-testid={`${testPrefix}-new`} type="password" autoComplete="new-password" autoFocus={!needsCurrent} value={pw}
        onChange={(e) => setPw(e.target.value)} placeholder="New password (8+ characters)" className={inputCls} />
      <input data-testid={`${testPrefix}-confirm`} type="password" autoComplete="new-password" value={pw2}
        onChange={(e) => setPw2(e.target.value)} placeholder="Type it again" className={inputCls} />
      {err && <p className="text-sm text-danger-700 flex items-start gap-1.5" data-testid={`${testPrefix}-error`}><ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" /> {err}</p>}
      <button data-testid={`${testPrefix}-submit`} type="submit" disabled={busy}
        className="w-full rounded-full bg-ink text-white font-semibold py-3 text-sm hover:bg-ink/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
        {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : (submitLabel || (user?.has_password ? "Change password" : "Save password"))}
      </button>
    </form>
  );
}
